import { resolveApiBaseUrl } from '@/lib/config/runtime';
import { setPrimaryToken } from '@/lib/auth/storage';
import {
  getLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
} from '@/lib/storage/web-storage';

import {
  applyPostAuthSessionFlags,
  getUtmValues,
  getValidCouponFromStorage,
  STORAGE_KEYS,
} from './login-storage';
import {
  resolveGooglePostAuthRedirect,
  resolvePostAuthRedirect,
} from './login-redirect';
import type { AuthFlowType } from './login-redirect';

export type AuthResponse = {
  status?: string;
  message?: string;
  token?: string;
  type?: string;
};

function encodeFormBody(data: Record<string, string | null | undefined>): string {
  return Object.entries(data)
    .filter(([, value]) => value !== undefined && value !== null)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
    .join('&');
}

async function postForm<T>(path: string, body: Record<string, string | null | undefined>): Promise<T> {
  const response = await fetch(`${resolveApiBaseUrl()}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: encodeFormBody(body),
  });
  return response.json() as Promise<T>;
}

function getTemporaryTokenValue(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  return getLocalStorageItem(STORAGE_KEYS.temporaryToken);
}

export async function signInWithCredentials(
  username: string,
  password: string,
  preferredRedirect?: string | null,
): Promise<{ ok: true; redirectTo: string } | { ok: false; message: string }> {
  const utm = getUtmValues();
  const temporary_token = getTemporaryTokenValue();
  const hadTemporaryToken = Boolean(temporary_token);

  const data = await postForm<AuthResponse>('/pro/auth/sign-in', {
    username,
    password,
    utm_source: utm.utm_source,
    utm_medium: utm.utm_medium,
    utm_campaign: utm.utm_campaign,
    temporary_token,
  });

  if (data.status !== 'success' || !data.token) {
    return { ok: false, message: data.message || 'Username and password do not match or you do not have an account yet.' };
  }

  setPrimaryToken(data.token);
  applyPostAuthSessionFlags({
    hadTemporaryToken,
    isSignup: false,
    isMobile: window.innerWidth < 768,
  });

  const coupon = getValidCouponFromStorage();
  return {
    ok: true,
    redirectTo: resolvePostAuthRedirect('signin', coupon, preferredRedirect),
  };
}

export async function signUpWithCredentials(
  name: string,
  username: string,
  password: string,
  preferredRedirect?: string | null,
): Promise<{ ok: true; redirectTo: string } | { ok: false; message: string }> {
  const utm = getUtmValues();
  const temporary_token = getTemporaryTokenValue();
  const hadTemporaryToken = Boolean(temporary_token);

  const data = await postForm<AuthResponse>('/pro/auth/sign-up', {
    name,
    username,
    password,
    utm_source: utm.utm_source,
    utm_medium: utm.utm_medium,
    utm_campaign: utm.utm_campaign,
    temporary_token,
  });

  if (data.status !== 'success' || !data.token) {
    return {
      ok: false,
      message: data.message || 'Unable to create account. Please try again.',
    };
  }

  setPrimaryToken(data.token);
  applyPostAuthSessionFlags({
    hadTemporaryToken,
    isSignup: true,
    isMobile: window.innerWidth < 768,
  });

  const coupon = getValidCouponFromStorage();
  if (coupon) {
    return {
      ok: true,
      redirectTo: resolvePostAuthRedirect('signup', coupon, preferredRedirect),
    };
  }

  return {
    ok: true,
    redirectTo: resolvePostAuthRedirect('signup', null, preferredRedirect),
  };
}

export async function signInWithGoogle(
  idToken: string,
  preferredRedirect?: string | null,
): Promise<{ ok: true; redirectTo: string } | { ok: false; message: string }> {
  const utm = getUtmValues();
  const temporary_token = getTemporaryTokenValue();
  const hadTemporaryToken = Boolean(temporary_token);

  const data = await postForm<AuthResponse>('/pro/auth/google', {
    id_token: idToken,
    utm_source: utm.utm_source,
    utm_medium: utm.utm_medium,
    utm_campaign: utm.utm_campaign,
    temporary_token,
  });

  if (data.status !== 'success' || !data.token) {
    return { ok: false, message: data.message || 'Google sign-in failed. Please try again.' };
  }

  setPrimaryToken(data.token);
  const isSignup = data.type !== 'sign-in';
  applyPostAuthSessionFlags({
    hadTemporaryToken,
    isSignup,
    isMobile: window.innerWidth < 768,
  });

  const coupon = getValidCouponFromStorage();
  return {
    ok: true,
    redirectTo: resolveGooglePostAuthRedirect(data.type, coupon, preferredRedirect),
  };
}

export async function verifyAccount(
  token: string,
): Promise<{ ok: true; message: string } | { ok: false; message: string }> {
  const data = await postForm<AuthResponse>('/pro/auth/verify', { token });
  if (data.status === 'success') {
    return { ok: true, message: data.message || 'Account activated successfully!' };
  }
  return {
    ok: false,
    message:
      data.message ||
      'Activation failed. Either the link is invalid or the user is already activated.',
  };
}

export async function forgotPassword(
  email: string,
): Promise<{ ok: true; message: string } | { ok: false; message: string }> {
  try {
    const data = await postForm<AuthResponse>('/pro/auth/forgot-password', { email });
    return {
      ok: true,
      message:
        data.message ||
        'You will get a password reset email if the email you provided is in our system.',
    };
  } catch {
    return { ok: false, message: 'Unable to send reset email. Please try again.' };
  }
}

export async function resetPassword(
  token: string,
  password: string,
  confirmPassword: string,
): Promise<{ ok: true; message: string } | { ok: false; message: string }> {
  if (password !== confirmPassword) {
    return { ok: false, message: "Passwords don't match." };
  }
  if (password.length < 8 || password.length > 24) {
    return { ok: false, message: 'Password must be between 8 and 24 characters.' };
  }

  try {
    const data = await postForm<AuthResponse>('/pro/auth/reset-password', {
      token,
      password,
      confirm_password: confirmPassword,
    });
    if (data.status === 'success') {
      return { ok: true, message: data.message || 'Password reset successfully.' };
    }
    return { ok: false, message: data.message || 'Unable to reset password. Please try again.' };
  } catch {
    return { ok: false, message: 'Unable to reset password. Please try again.' };
  }
}

export function validateEmail(email: string): boolean {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
}

export type PasswordStrength = {
  label: string;
  color: string;
};

export function getPasswordStrength(password: string): PasswordStrength | null {
  if (!password) {
    return null;
  }

  let strength = 0;
  if (password.length <= 5) {
    return { label: 'Too short', color: '#ff0000' };
  }
  if (password.length >= 8) {
    strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[!@#$%^&*()]/.test(password)) strength++;
  }

  switch (strength) {
    case 0:
      return { label: 'Weak', color: '#FF8000' };
    case 1:
    case 2:
      return { label: 'Medium', color: '#A0FF00' };
    case 3:
    case 4:
      return { label: 'Strong', color: '#30FF00' };
    case 5:
      return { label: 'Very Strong', color: '#10FF00' };
    default:
      return { label: 'Weak', color: '#FF8000' };
  }
}

export function getTabCopy(tab: AuthFlowType): { title: string; subtitle: string } {
  if (tab === 'signup') {
    return { title: "Let's Get Started!", subtitle: 'Create Your Account' };
  }
  return { title: 'Welcome Back!', subtitle: 'Log in to your Resume Builder account' };
}
